<template>
  <div class="predictive-search">
    <button
      v-if="!isMobile && searchStatus === 'done'"
      type="button"
      class="predictive-search__close"
      @click="closeSearchModal"
    >
      <icon-close />
    </button>

    <div
      v-if="isMobile"
      class="predictive-search__header"
    >
      <div class="predictive-search__header-inner">
        <div class="predictive-search__left">
          <button
            class="predictive-search__close icon-button"
            type="button"
            @click="openOverlay({
              component: 'menu-drawer',
              ignoreDismissed: true,
              namespace: 'menuDrawer',
            })"
          >
            <icon-menu />
          </button>

          <button
            class="predictive-search__search icon-button"
            type="button"
            @click="closeSearchModal"
          >
            <icon-close />
          </button>
        </div>

        <a
          :href="$variable('routes.root')"
          class="predictive-search__center"
        >
          <span
            class="visually-hidden"
            v-text="$variable('store.shopName')"
          />

          <brand-logo />
        </a>

        <div class="predictive-search__right">
          <a
            class="predictive-search__account icon-button"
            :href="$variable('routes.account')"
          >
            <icon-account />
          </a>

          <button
            class="predictive-search__cart icon-button"
            type="button"
            @click="handleCartClick"
          >
            <icon-cart />
          </button>
        </div>
      </div>

      <form
        class="predictive-search__search"
        :action="$variable('routes.search')"
        method="GET"
      >
        <icon-search
          v-if="searchStatus === 'ready'"
        />

        <button
          v-if="searchStatus === 'done'"
          type="button"
          class="predictive-search__search-clear text-body-xs-regular"
          @click="clearSearch"
          v-text="$string('predictiveSearch.clear')"
        />

        <input
          ref="searchInput"
          class="predictive-search__search-input"
          type="search"
          name="q"
          :placeholder="$string('general.site_header.mobile_search')"
          :value="searchTerm"
          autocomplete="off"
          @input="updateSearchValue($event)"
        >
      </form>
    </div>

    <div class="predictive-search__body">
      <div class="predictive-search__main">
        <div
          v-if="searchStatus === 'ready' && isMobile"
          class="predictive-search__popular predictive-search__popular--mobile"
        >
          <h3
            class="predictive-search__pages-title text-body-s-bold"
            v-text="$string('predictiveSearch.popular_searches')"
          />

          <ul class="predictive-search__popular-list">
            <li
              v-for="(term, index) of popularTerms.slice(0, 4)"
              :key="index"
              class="predictive-search__popular-list-item"
            >
              <a
                :href="`${currentURL}search?q=${term}`"
                class="predictive-search__popular-link text-body-s-regular"
                v-text="term"
              />
            </li>
          </ul>
        </div>

        <div
          v-if="searchResults.products.length > 0 && searchStatus === 'done' && isMobile"
          class="predictive-search__suggestions predictive-search__suggestions--mobile"
        >
          <h3
            class="predictive-search__suggestions-title text-body-s-bold"
            v-text="$string('predictiveSearch.suggestions')"
          />

          <ul
            v-if="searchResults.queries.length > 0"
            class="predictive-search__suggestions-list"
          >
            <li
              v-for="(query, index) of searchResults.queries.slice(0, 4)"
              :key="index"
              class="predictive-search__suggestions-list-item"
            >
              <a
                :href="`${currentURL}search?q=${query.text}&${query.trackingParameters}`"
                class="predictive-search__query text-body-s-regular"
                v-text="query.text"
              />
            </li>
          </ul>

          <p
            v-if="searchResults.queries.length === 0"
            class="predictive-search__empty-list-subtitle text-body-s-regular"
            v-text="$string('predictiveSearch.no_suggestions')"
          />
        </div>

        <div
          class="predictive-search__products"
          :class="{ 'predictive-search__products--length': searchResults.products.length > 0 }"
        >
          <h3
            v-if="searchStatus === 'done'"
            class="predictive-search__products-title text-body-s-bold"
            v-text="$string('predictiveSearch.products')"
          />

          <ul
            v-if="searchResults.products.length > 0 && searchStatus === 'done'"
            class="predictive-search__product-list"
          >
            <li
              v-for="(product, index) of searchResults.products.slice(0, 3)"
              :key="index"
              class="predictive-search__product-list-item"
            >
              <product-card
                :product="product"
                :sizes="sizes"
              />
            </li>
          </ul>

          <p
            v-if="searchResults.products.length === 0 && searchStatus === 'done'"
            class="predictive-search__empty-list-subtitle text-body-s-regular"
            v-text="$string('predictiveSearch.no_products')"
          />
        </div>
      </div>

      <div class="predictive-search__aside">
        <div
          v-if="searchResults.products.length > 0 && searchStatus === 'done' && !isMobile"
          class="predictive-search__suggestions predictive-search__suggestions--desktop"
        >
          <h3
            class="predictive-search__suggestions-title text-body-s-bold"
            v-text="$string('predictiveSearch.suggestions')"
          />

          <ul
            v-if="searchResults.queries.length > 0"
            class="predictive-search__suggestions-list"
          >
            <li
              v-for="(query, index) of searchResults.queries.slice(0, 4)"
              :key="index"
              class="predictive-search__suggestions-list-item"
            >
              <a
                :href="`${currentURL}search?q=${query.text}&${query.trackingParameters}`"
                class="predictive-search__query text-body-s-regular"
                v-text="query.text"
              />
            </li>
          </ul>

          <p
            v-if="searchResults.queries.length === 0"
            class="predictive-search__empty-list-subtitle text-body-s-regular"
            v-text="$string('predictiveSearch.no_suggestions')"
          />
        </div>

        <div
          v-if="searchResults.products.length > 0 && searchStatus === 'done'"
          class="predictive-search__pages"
        >
          <h3
            class="predictive-search__pages-title text-body-s-bold"
            v-text="$string('predictiveSearch.pages')"
          />

          <ul
            v-if="searchResults.pages.length > 0"
            class="predictive-search__pages-list"
          >
            <li
              v-for="(page, index) of searchResults.pages.slice(0, 4)"
              :key="index"
              class="predictive-search__pages-list-item"
            >
              <a
                :href="page.onlineStoreUrl"
                class="predictive-search__page text-body-s-regular"
                v-text="page.title"
              />
            </li>
          </ul>

          <p
            v-if="searchResults.pages.length === 0"
            class="predictive-search__empty-list-subtitle text-body-s-regular"
            v-text="$string('predictiveSearch.no_pages')"
          />
        </div>

        <div
          v-if="
            searchResults.products.length === 0 &&
              searchStatus === 'done' &&
              popularTerms.length"
          class="predictive-search__popular predictive-search__popular--desktop"
        >
          <h3
            class="predictive-search__pages-title text-body-s-bold"
            v-text="$string('predictiveSearch.popular_searches')"
          />

          <ul class="predictive-search__popular-list">
            <li
              v-for="(term, index) of popularTerms.slice(0, 4)"
              :key="index"
              class="predictive-search__popular-list-item"
            >
              <a
                :href="`${currentURL}search?q=${term}`"
                class="predictive-search__popular-link text-body-s-regular"
                v-text="term"
              />
            </li>
          </ul>
        </div>

        <div
          v-if="
            searchResults.products.length === 0 &&
              searchStatus === 'done' &&
              helpfulLinks.length"
          class="predictive-search__helpful-links"
        >
          <h3
            class="predictive-search__pages-title text-body-s-bold"
            v-text="$string('predictiveSearch.helpful_link')"
          />

          <ul class="predictive-search__helpful-list">
            <li
              v-for="(term, index) of helpfulLinks.slice(0, 3)"
              :key="index"
              class="predictive-search__helpful-list-item"
              v-html="term"
            />
          </ul>
        </div>
      </div>
    </div>

    <div
      v-if="searchResults.products.length > 0 && searchStatus === 'done'"
      class="predictive-search__footer"
    >
      <btn
        class="predictive-search__view-all"
        :label="$string('predictiveSearch.view_all')"
        :url="`${currentURL}search?q=${searchTerm}`"
        modifiers="primary block"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Overlay to display predictive search results.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'
import predictiveSearchQuery from '~graphql/predictiveSearch.gql'
import { formatPredictiveSearchResults } from '~/helpers/format'
import Btn from '~global/btn/btn'
import IconMenu from '~icons/general/menu.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconSearch from '~icons/general/search.svg'
import BrandLogo from '~icons/general/brand-logo.svg'

import ProductCard from '~async/product-card/product-card'
import timings from '~/config/timings'

export default {
  name: 'PredictiveSearch',

  components: {
    ProductCard,
    Btn,
    IconMenu,
    IconClose,
    IconAccount,
    IconCart,
    IconSearch,
    BrandLogo,
  },

  props: {
    searchTerm: {
      type: String,
      default: '',
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'close-search-modal': () => true,
    'clear-mobile-search': () => true,
    'update-search-value': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      currentURL: '',
      state: 'loading',
      searchResults: {
        products: [],
        pages: [],
        queries: [],
      },
      searchVariables: {
        country: cnvs.store.country,
        language: cnvs.store.language,
        query: '',
        types: [
          'PRODUCT',
          'PAGE',
          'QUERY',
        ],
        searchableFields: [
          'TITLE',
          'PRODUCT_TYPE',
          'VARIANTS_TITLE',
          'VENDOR',
          'BODY',
        ],
      },
    }
  },

  computed: {

    /**
     * Map Vuex Getters.
     */
    ...mapGetters({
      searchStatus: 'search/getSearchStatus',
      searchActive: 'search/getSearchActive',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns popular terms that are set in theme settings
     * @returns {Array}
     */
    popularTerms() {
      return cnvs.search.popularTerms
    },

    /**
     * Returns helpful links that are set in theme settings
     * @returns {Array}
     */
    helpfulLinks() {
      return cnvs.search.helpfulLinks
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.s}) 33vw, 30vw`
    },
  },

  watch: {

    /**
     * Watch for changes in entered search term.
     */
    searchTerm() {
      if (!this.searchTerm) {
        return
      }

      this.searchVariables.query = this.searchTerm
      this.runSearchQuery()
    },
  },

  mounted() {
    this.currentURL = `//${window.location.host}${window.cnvs.routes.normalisedRoot}`
    this.overlay.addCallback(this.closeSearchModal, 'overlay')

    document.addEventListener('focus', () => {
      if (!this.isMobile && !this.$refs.searchInput) {
        return
      }

      if (this.$refs.searchInput === document.activeElement) {
        document.documentElement.style
          .setProperty('--predictive-search-height', 'var(--viewport-height)')
      } else {
        document.documentElement.style
          .setProperty('--predictive-search-height', 'calc(var(--viewport-height) - var(--spacing-5xl))')
      }
    }, true)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setSearchActive: 'search/setSearchActive',
      setSearchStatus: 'search/setSearchStatus',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Run search query and sets the results
     */
    async runSearchQuery() {
      try {
        const response = await cnvs.Query({
          query: predictiveSearchQuery,
          variables: this.searchVariables,
        })

        this.searchResults = formatPredictiveSearchResults(response)
        this.setSearchStatus('done')

      } catch (error) {
        cnvs.ReportError('Failed to perform search', error)
      } finally {
        this.state = 'ready'
      }
    },

    /**
     * Closes predictive search overlay
     */
    closeSearchModal() {
      this.$emit('close-search-modal')
    },

    /**
     * Update search value.
     * @param {Object} value - search input event.
     */
    updateSearchValue(event) {
      this.$emit('update-search-value', event.target.value)
    },

    /**
     * Clear search term by emiting event and changing search state.
     */
    clearSearch() {
      this.$emit('clear-mobile-search')
      this.setSearchStatus('ready')
    },

    /**
     * Handle overlays on cart click.
     */
    handleCartClick() {
      this.$emit('close-search-modal')

      setTimeout(() => {
        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })
      }, timings.slow)
    },
  },
}
</script>

<style lang="scss">
@import './predictive-search';
</style>
